import { useChannelActionContext } from '../../../context/ChannelActionContext';

import type React from 'react';
import type { UserResponse } from 'belo-js-sdk';

import type { ReactEventHandler } from '../types';

import type { StreamMessage } from '../../../context/ChannelStateContext';

import type { DefaultBeloChatGenerics } from '../../../types/types';

export type CustomMentionHandler<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = (event: React.BaseSyntheticEvent, mentioned_users: UserResponse<BeloChatGenerics>[]) => void;

export type MentionedUserEventHandler<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
> = (event: React.BaseSyntheticEvent, mentionedUsers: UserResponse<BeloChatGenerics>[]) => void;

function createEventHandler<
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(
  fn?: CustomMentionHandler<BeloChatGenerics>,
  message?: StreamMessage<BeloChatGenerics>,
): ReactEventHandler {
  return (event) => {
    if (typeof fn !== 'function' || !message?.mentioned_users?.length) {
      return;
    }
    fn(event, message.mentioned_users);
  };
}

export const useMentionsHandler = <
  BeloChatGenerics extends DefaultBeloChatGenerics = DefaultBeloChatGenerics
>(
  message?: StreamMessage<BeloChatGenerics>,
  customMentionHandler?: {
    onMentionsClick?: CustomMentionHandler<BeloChatGenerics>;
    onMentionsHover?: CustomMentionHandler<BeloChatGenerics>;
  },
) => {
  const {
    onMentionsClick: contextOnMentionsClick,
    onMentionsHover: contextOnMentionsHover,
  } = useChannelActionContext<BeloChatGenerics>('useMentionsHandler');

  const onMentionsClick =
    customMentionHandler?.onMentionsClick || contextOnMentionsClick || (() => null);

  const onMentionsHover =
    customMentionHandler?.onMentionsHover || contextOnMentionsHover || (() => null);

  return {
    onMentionsClick: createEventHandler(onMentionsClick, message),
    onMentionsHover: createEventHandler(onMentionsHover, message),
  };
};
